import {FC, useState, useCallback} from "react";
import {Box, BoxProps, LinearProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {useOwnedCohortId} from "@/app2/shared/hooks";
import {useGetCohortSettings} from "@/app2/shared/data";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import AdminBulkPlaylistEnrollmentConfirmationModal from "../BulkEnrollmentConfirmationModal";
import {BulkPlaylistUnenrollmentConfirmationModal} from "./BulkPlaylistUnenrollmentConfirmationModal";
import {BulkPlaylistCompletionConfirmationModal} from "./BulkPlaylistCompletionConfirmationModal";
import {PlaylistEnrollmentsGrid} from "../PlaylistEnrollmentsGrid";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		display: "flex",
		flexDirection: "column",
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small,
		"& svg path": {
			fill: theme.palette.grey[500]
		}
	}
}));

interface ICustomProps {
	playlistId: number;
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const EnrollmentTab: FC<IProps> = ({playlistId, ...baseProps}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const [bulkEnrollModalOpen, setBulkEnrollModalOpen] = useState(false);
	const [bulkUnEnrollModalOpen, setBulkUnEnrollModalOpen] = useState(false);
	const [bulkCompletionModalOpen, setBulkCompletionModalOpen] = useState(false);
	const [selected, setSelected] = useState<number[]>([]);
	const [selectedSingular, setSelectedSingular] = useState<number | undefined>();
	const cohortId = useOwnedCohortId();
	const {data: cohortSettings} = useGetCohortSettings(cohortId ?? 0, {
		enabled: !!cohortId
	});
	const {errorSnackbar} = useCustomSnackbar();

	const handleEnrollmentClose = () => {
		setSelectedSingular(undefined);
		setBulkEnrollModalOpen(false);
	};

	const handleUnenrollmentClose = () => {
		setSelectedSingular(undefined);
		setBulkUnEnrollModalOpen(false);
	};

	const handleEnroll = useCallback((userIds: number[]) => {
		setSelected(userIds);
		setBulkEnrollModalOpen(true);
	}, []);

	const handleUnenroll = useCallback((userIds: number[]) => {
		setSelected(userIds);
		setBulkUnEnrollModalOpen(true);
	}, []);

	const handleMarkComplete = useCallback((userIds: number[]) => {
		setSelected(userIds);
		setBulkCompletionModalOpen(true);
	}, []);

	if (!cohortId)
		return <LinearProgress />;

	const activeUserIds = (selectedSingular ? [selectedSingular] : selected).filter(
		(id): id is number => typeof id === "number"
	);

	return (
		<Box {...baseProps}>
			<Box className={classes.contentBox}>
				<PlaylistEnrollmentsGrid
					playlistId={playlistId}
					cohortId={cohortId}
					organization={cohortSettings}
					isPlaylistPublished={true}
					selected={selected}
					setSelected={setSelected}
					setSelectedSingular={setSelectedSingular}
					handleEnroll={handleEnroll}
					handleUnenroll={handleUnenroll}
					handleMarkComplete={handleMarkComplete}
				/>

				<AdminBulkPlaylistEnrollmentConfirmationModal
					open={bulkEnrollModalOpen}
					onClose={handleEnrollmentClose}
					selectedUserIds={activeUserIds}
					playlistId={playlistId}
				/>

				<BulkPlaylistUnenrollmentConfirmationModal
					open={bulkUnEnrollModalOpen}
					onClose={handleUnenrollmentClose}
					selectedUserIds={activeUserIds}
					playlistId={playlistId}
				/>

				<BulkPlaylistCompletionConfirmationModal
					open={bulkCompletionModalOpen}
					onClose={() => {
						setSelected([]);
						setSelectedSingular(undefined);
						setBulkCompletionModalOpen(false);
					}}
					selectedUserIds={selectedSingular ? [selectedSingular] : selected}
					playlistId={playlistId}
				/>
			</Box>
		</Box>
	);
};
